import {
    signInWithPopup,
    signInWithCredential,
    signOut as firebaseSignOut,
    onAuthStateChanged,
    GoogleAuthProvider,
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp, onSnapshot, collection } from 'firebase/firestore';
import { auth, googleProvider, db } from './firebase';
import { Capacitor } from '@capacitor/core';

// Google 로그인 (네이티브/웹 분기 처리)
export async function signInWithGoogle() {
    try {
        let user;

        if (Capacitor.isNativePlatform()) {
            // === 네이티브 앱 (Android/iOS) ===
            // skipNativeAuth: true → Firebase Auth 수동 연결 필수
            const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
            const result = await FirebaseAuthentication.signInWithGoogle();

            // skipNativeAuth: true → 반드시 수동으로 Firebase Auth에 credential 전달
            if (!result.credential?.idToken) {
                throw new Error('Google 인증 토큰을 가져올 수 없습니다.');
            }
            const credential = GoogleAuthProvider.credential(result.credential.idToken);
            const userCredential = await signInWithCredential(auth, credential);
            user = userCredential.user;

            // 네이티브 Firebase Auth에도 로그인 (위젯 등 네이티브 코드용)
            try {
                await FirebaseAuthentication.signInWithCredential({
                    credential: { providerId: 'google.com', idToken: result.credential.idToken },
                });
            } catch (nativeErr) {
                console.warn('네이티브 Firebase Auth 동기화 실패 (위젯 미작동 가능):', nativeErr);
            }
        } else {
            // === 웹 브라우저 ===
            const result = await signInWithPopup(auth, googleProvider);
            user = result.user;
        }

        // Firestore에 사용자 존재 확인
        const userDoc = await getDoc(doc(db, 'users', user.uid));

        return {
            user,
            isNewUser: !userDoc.exists(),
        };
    } catch (error) {
        console.error('로그인 실패:', error);
        throw error;
    }
}

// 닉네임 설정 (신규 사용자)
export async function setupNickname(userId, nickname, email) {
    // 닉네임 형식 검증 (클라이언트 우회 방지)
    const trimmed = nickname.trim();
    if (trimmed.length < 2) throw new Error('닉네임은 최소 2자 이상이어야 합니다.');
    const points = [...trimmed].reduce((sum, ch) => sum + (/[가-힣ㄱ-ㅎㅏ-ㅣ]/.test(ch) ? 2 : 1), 0);
    if (points > 12) throw new Error('닉네임이 너무 깁니다. (한글 6자 / 영문 12자 이내)');
    nickname = trimmed; // 이후 모든 작업에 trimmed 사용

    const { runTransaction } = await import('firebase/firestore');
    await runTransaction(db, async (transaction) => {
        const nicknameRef = doc(db, 'nicknames', nickname);
        const nicknameSnap = await transaction.get(nicknameRef);

        if (nicknameSnap.exists()) {
            throw new Error('이미 사용 중인 닉네임입니다.');
        }

        transaction.set(nicknameRef, {
            uid: userId,
            createdAt: serverTimestamp(),
        });

        transaction.set(doc(db, 'users', userId), {
            uid: userId,
            email: email,
            nickname: nickname,
            plan: 'free',
            planExpiresAt: null,
            planUpdatedAt: null,
            nicknameChangedAt: serverTimestamp(),
            googleCalendarId: '',
            freeTrialUsed: true,
            freeTrialExpiry: Date.now() + 7 * 24 * 60 * 60 * 1000,
            welcomePopupShown: false, // 환영 팝업 노출 여부 (1회성)
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        });

        // 관리자 명의의 환영 혜택 안내 DM 생성 (기존 DM 스키마와 동일한 필드명 사용)
        const dmRef = doc(collection(db, 'users', userId, 'notifications'));
        transaction.set(dmRef, {
            type: 'dm',
            senderUid: 'system',
            senderNickname: 'TodoList Share 관리자',
            senderEmail: '',
            message: '가입을 환영합니다! 🎉\n\n선물로 7일간 Pro 등급의 모든 기능을 무료로 이용하실 수 있도록 활성화해드렸습니다.\n\n✅ 하단 배너 광고 완전 제거\n✅ 프로젝트 최대 10개 생성\n✅ 무제한 체크리스트\n✅ 채팅 내 이미지 첨부\n✅ 구글 캘린더 공유\n\n즐거운 일정 관리 되시길 바랍니다!',
            read: false,
            createdAt: serverTimestamp(),
        });
    });
}

// 로그아웃
export async function signOutUser() {
    if (Capacitor.isNativePlatform()) {
        try {
            const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
            await FirebaseAuthentication.signOut();
        } catch (e) {
            console.warn('네이티브 signOut 실패 (비필수):', e);
        }
    }
    await firebaseSignOut(auth);
}

// 인증 상태 감시
export function onAuthChange(callback) {
    return onAuthStateChanged(auth, callback);
}

// 사용자 정보 가져오기
export async function getUserProfile(userId) {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
        return { id: userDoc.id, ...userDoc.data() };
    }
    return null;
}

// 사용자 정보 실시간 감시 (인앱 구독/체험판/리워드 동적으로 동기화 용도)
export function subscribeToUserProfile(userId, callback, onError) {
    return onSnapshot(doc(db, 'users', userId), (docSnap) => {
        if (docSnap.exists()) {
            callback({ id: docSnap.id, ...docSnap.data() });
        } else {
            callback(null);
        }
    }, (error) => {
        console.error('subscribeToUserProfile error:', error);
        // 에러 시 1회성 getDoc 폴백
        getDoc(doc(db, 'users', userId)).then(snap => {
            if (snap.exists()) callback({ id: snap.id, ...snap.data() });
            else callback(null);
        }).catch(() => callback(null));
        if (onError) onError(error);
    });
}
